import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Clock, Rocket, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, suggestionService, type SuggestionDto } from '../services/api';

const STATUS_OPTIONS = [
  { value: 0, label: 'New' },
  { value: 1, label: 'UnderReview' },
  { value: 2, label: 'Planned' },
  { value: 3, label: 'Implemented' },
  { value: 4, label: 'Declined' },
];

const STATUS_COLOURS: Record<string, string> = {
  New:         'bg-blue-100 text-blue-700',
  UnderReview: 'bg-yellow-100 text-yellow-700',
  Planned:     'bg-purple-100 text-purple-700',
  Implemented: 'bg-green-100 text-green-700',
  Declined:    'bg-red-100 text-red-700',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  New:         <Clock className="w-3 h-3" />,
  UnderReview: <Clock className="w-3 h-3" />,
  Planned:     <Rocket className="w-3 h-3" />,
  Implemented: <CheckCircle className="w-3 h-3" />,
  Declined:    <XCircle className="w-3 h-3" />,
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: '0', label: 'New' },
  { key: '1', label: 'Under Review' },
  { key: '2', label: 'Planned' },
  { key: '3', label: 'Implemented' },
  { key: '4', label: 'Declined' },
];

export default function AdminSuggestionsPage() {
  const { getToken, account } = useAppAuth();
  const client = createApiClient(getToken);
  const svc = suggestionService(client);
  const qc = useQueryClient();

  const [filter, setFilter]         = useState('all');
  const [page, setPage]             = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusDraft, setStatusDraft] = useState<number>(1);
  const [notesDraft, setNotesDraft]   = useState('');
  const [processing, setProcessing]   = useState<string | null>(null);

  const statusFilter = filter === 'all' ? undefined : Number(filter);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin-suggestions', filter, page],
    queryFn: () => svc.getAllAdmin(statusFilter, page, 50),
    enabled: !!account,
  });

  const toggleExpand = (s: SuggestionDto) => {
    if (expandedId === s.suggestionId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(s.suggestionId);
    setStatusDraft(STATUS_OPTIONS.find((o) => o.label === s.status)?.value ?? 1);
    setNotesDraft(s.adminNotes ?? '');
  };

  const update = async (id: string) => {
    setProcessing(id);
    try {
      await svc.updateStatus(id, statusDraft, notesDraft.trim() || undefined);
      qc.invalidateQueries({ queryKey: ['admin-suggestions'] });
      setExpandedId(null);
    } catch (e: any) {
      alert(e?.response?.data?.title ?? 'Failed to update suggestion status.');
    } finally {
      setProcessing(null);
    }
  };

  const counts = STATUS_OPTIONS.reduce<Record<string, number>>((acc, o) => {
    acc[o.label] = items.filter((i) => i.status === o.label).length;
    return acc;
  }, {});

  return (
    <div className="page-shell">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suggestions &amp; Feedback</h1>
          <p className="text-sm text-gray-500 mt-1">Review submissions, triage status, and leave internal notes.</p>
        </div>
        <div className="flex gap-4 shrink-0">
          {STATUS_OPTIONS.map((o) => (
            <div key={o.label} className="text-center">
              <div className="text-xl font-bold text-gray-700">{counts[o.label] ?? 0}</div>
              <div className="text-xs text-gray-400">{o.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setPage(1); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              filter === f.key
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No suggestions on this page.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm divide-y divide-gray-100">
          {items.map((s) => {
            const isExpanded  = expandedId === s.suggestionId;
            const isProcessing = processing === s.suggestionId;
            return (
              <div key={s.suggestionId}>
                <button
                  onClick={() => toggleExpand(s)}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50/50 transition-colors flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{s.category}</span>
                      <span className="font-semibold text-gray-900 text-sm">{s.title}</span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-1">{s.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {s.fullName ?? 'Unknown user'} ({s.userRole}) ·{' '}
                      {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOURS[s.status]}`}>
                      {STATUS_ICONS[s.status]}
                      {s.status}
                    </span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{s.description}</p>
                    <div className="flex items-start gap-4 flex-wrap">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Status</label>
                        <select
                          value={statusDraft}
                          onChange={(e) => setStatusDraft(Number(e.target.value))}
                          className="text-xs border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500"
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1 min-w-48">
                        <label className="block text-xs text-gray-600 mb-1">Internal notes (not shown to submitter)</label>
                        <input
                          type="text"
                          value={notesDraft}
                          onChange={(e) => setNotesDraft(e.target.value)}
                          placeholder="e.g. Added to Q3 roadmap"
                          className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => update(s.suggestionId)}
                        disabled={isProcessing}
                        className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                      >
                        {isProcessing
                          ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <CheckCircle className="w-3.5 h-3.5" />}
                        Update Status
                      </button>
                      <button
                        onClick={() => setExpandedId(null)}
                        className="text-xs text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && (items.length === 50 || page > 1) && (
        <div className="flex justify-center gap-3 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">Page {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={items.length < 50}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
